import { bind, define, getHost, html, prop, useEmit, useSlots, watchEffect } from '@vielzeug/ore';
import { computed } from '@vielzeug/ripple';
import { createInteraction } from '../../core';
import { disablableBundle, loadableBundle, themableBundle } from '../../shared';
import {
  colorThemeMixin,
  elevationMixin,
  frostVariantMixin,
  paddingMixin,
  reducedMotionMixin,
  roundedVariantMixin,
} from '../../styles';
import type { ElevationLevel, PaddingSize, ThemeColor } from '../../types';
import componentStyles from './card.css?inline';

const INTERACTIVE_DESCENDANT_SELECTOR =
  'button, a[href], input, select, textarea, summary, [role="button"], [role="link"], [contenteditable=""], [contenteditable="true"]';

function isNestedInteractiveTarget(host: HTMLElement, event: Event): boolean {
  for (const node of event.composedPath()) {
    if (!(node instanceof HTMLElement)) continue;

    if (node === host) return false;

    if (node.matches(INTERACTIVE_DESCENDANT_SELECTOR) || node.closest(INTERACTIVE_DESCENDANT_SELECTOR)) {
      return true;
    }
  }

  return false;
}

/** Card component properties */

export type OreCardEvents = {
  activate: { originalEvent: MouseEvent | KeyboardEvent; trigger: 'pointer' | 'keyboard' };
};

export type OreCardProps = {
  /** Theme color */
  color?: ThemeColor;
  /** Disable interaction */
  disabled?: boolean;
  /** Shadow elevation level (0-5) */
  elevation?: ElevationLevel;
  /** Make the card interactive (role=button, keyboard nav, emits activate) */
  interactive?: boolean;
  /** Show a loading progress bar */
  loading?: boolean;
  /** Card orientation */
  orientation?: 'horizontal';
  /** Internal padding size */
  padding?: PaddingSize;
  /** Visual style variant */
  variant?: 'solid' | 'flat' | 'glass' | 'frost';
};

/**
 * A versatile card container with semantic slots for media, header, body, footer, and actions.
 *
 * @element ore-card
 *
 * @attr {string} variant - Visual variant: 'solid' | 'flat' | 'glass' | 'frost'
 * @attr {string} color - Theme color: 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error'
 * @attr {string} padding - Internal padding: 'none' | 'sm' | 'md' | 'lg' | 'xl'
 * @attr {string} elevation - Shadow elevation: '0' | '1' | '2' | '3' | '4' | '5'
 * @attr {string} orientation - Card layout: 'horizontal'
 * @attr {boolean} interactive - Enable pointer/keyboard activation
 * @attr {boolean} disabled - Disable card interaction
 * @attr {boolean} loading - Show loading progress bar
 *
 * @fires activate - Emitted when an interactive card is activated. detail: { originalEvent: MouseEvent | KeyboardEvent; trigger: 'pointer' | 'keyboard' }
 *
 * @slot media - Media content displayed at top/left
 * @slot header - Card header (title, subtitle)
 * @slot - Main card content
 * @slot footer - Card footer content
 * @slot actions - Action buttons or links
 *
 * @cssprop --card-bg - Background color
 * @cssprop --card-color - Text color
 * @cssprop --card-border - Border width
 * @cssprop --card-border-color - Border color
 * @cssprop --card-radius - Border radius
 * @cssprop --card-padding - Internal padding
 * @cssprop --card-shadow - Box shadow
 * @cssprop --card-hover-shadow - Shadow on hover
 *
 * @part card - Card root container.
 * @part loading-bar - Loading progress bar.
 * @part media - Media slot container.
 * @part body - Body content container.
 * @part header - Header container.
 * @part content - Content container.
 * @part footer - Footer container.
 * @part actions - Actions slot container.
 * @example
 * ```html
 * <ore-card elevation="2"><h3 slot="header">Title</h3><p>Content</p></ore-card>
 * <ore-card interactive color="primary"><h3 slot="header">Click me</h3></ore-card>
 * <ore-card variant="frost" color="secondary">Frosted card</ore-card>
 * ```
 */
export const CARD_TAG = 'ore-card' as const;
define<OreCardProps>(CARD_TAG, {
  props: {
    ...themableBundle,
    ...disablableBundle,
    ...loadableBundle,
    elevation: prop.number<ElevationLevel>(),
    interactive: prop.bool(false),
    orientation: prop.string<'horizontal'>(),
    padding: prop.string<PaddingSize>(),
    variant: prop.string<'solid' | 'flat' | 'glass' | 'frost'>(),
  },

  setup(props) {
    const el = getHost();
    const emit = useEmit<OreCardEvents>();
    const slots = useSlots();
    const watch = watchEffect;

    bind({
      attr: {
        ariaBusy: () => (props.loading.value ? 'true' : 'false'),
        ariaDisabled: () => (props.interactive.value ? String(props.disabled.value) : null),
        role: () => (props.interactive.value ? 'button' : null),
      },
    });

    watch(() => {
      if (props.interactive.value) {
        el.setAttribute('tabindex', props.disabled.value ? '-1' : '0');
      } else {
        el.removeAttribute('tabindex');
      }
    });

    // ────────────────────────────────────────────────────────────────
    // Event Handlers
    // ────────────────────────────────────────────────────────────────

    const pressControl = createInteraction({
      disabled: computed(() => !props.interactive.value || Boolean(props.disabled.value)),
      onPress: (originalEvent, trigger) => {
        emit('activate', { originalEvent, trigger });
      },
    });

    const handleClick = (e: MouseEvent) => {
      if (!props.interactive.value || props.disabled.value) return;

      if (isNestedInteractiveTarget(el, e)) return;

      pressControl.handleClick(e);
    };

    const handleKeydown = (e: KeyboardEvent) => {
      pressControl.handleKeydown(e);
    };

    bind({
      on: {
        click: (e: MouseEvent) => handleClick(e),
        keydown: (e: KeyboardEvent) => handleKeydown(e),
      },
    });

    // ────────────────────────────────────────────────────────────────
    // Template
    // ────────────────────────────────────────────────────────────────

    return html`
      <div class="card" part="card">
        <div class="loading-bar" part="loading-bar"></div>
        <div class="card-media" part="media" ?hidden="${() => !slots.has('media').value}">
          <slot name="media"></slot>
        </div>
        <div class="card-body" part="body">
          <div class="card-header" part="header" ?hidden="${() => !slots.has('header').value}">
            <slot name="header"></slot>
          </div>
          <div class="card-content" part="content" ?hidden="${() => !slots.has().value}">
            <slot></slot>
          </div>
          <div class="card-footer" part="footer" ?hidden="${() => !slots.has('footer').value}">
            <slot name="footer"></slot>
          </div>
          <div class="card-actions" part="actions" ?hidden="${() => !slots.has('actions').value}">
            <slot name="actions"></slot>
          </div>
        </div>
      </div>
    `;
  },

  styles: [
    colorThemeMixin,
    elevationMixin,
    paddingMixin,
    roundedVariantMixin,
    frostVariantMixin('.card'),
    reducedMotionMixin,
    componentStyles,
  ],
});
